// New version src/Common/utilities/core.service.ts CommonUtilitiesCore
// Old version src/CaseDotStar.ServicePackages.Frontend.Common/scripts/common/services/utilities_service.js Utilities

import {
	UntypedFormArray,
	UntypedFormGroup,
} from '@angular/forms';
import {
	cloneDeep,
	compact,
	defaults,
	extend,
	find,
	forEach,
	get,
	isArray,
	isEmpty,
	isEqual,
	isNaN,
	isNull,
	isObject,
	isUndefined,
	merge,
	slice,
	uniqueId,
} from 'lodash';

import { ICommonKeyValues } from '../@types/core';

interface ICommonServiceCompareModelParams<T> {
	originalItems?: T[];
	itemsField: string;
	fieldId: string;
	addedItemsField: string;
	deletedItemsField: string;
}

class CommonUtilitiesCore {
	private hiddenDiv: HTMLElement = document.createElement('div');

	constructor () {
		this.hiddenDiv.className = 'g-hidden-measure-text';
		document.body.appendChild(this.hiddenDiv);
	}

	hasValue (value: any): boolean {
		return !isUndefined(value) && !isNull(value) && !isNaN(value) && value !== '';
	}

	getDataFromCurrentRoute ($uiRouter: any): ICommonKeyValues {
		const path = $uiRouter.globals.$current.path;
		let data = {};

		forEach(path, (state) => {
			if (state.data) {
				data = extend(data, state.data);
			}
		});

		return data;
	}

	getQueryStringKeyValue (key: string): string | null {
		const reg = new RegExp('[?&]' + key + '=([^&#]*)', 'i');
		const str = reg.exec(window.location.href);

		return str ? decodeURIComponent(str[1]) : null;
	}

	isOuterClick (event: Event, owner: string | JQuery<any>, context: string | JQuery<any> = 'body'): boolean {
		const $context = $(context);

		const $eventTarget = $(event.target! as HTMLElement);
		const isElementExist = !!$context.find($eventTarget).length || $eventTarget.is($context);

		return isElementExist && !$eventTarget.is(owner) && !$eventTarget.closest(owner).length;
	}

	getTempId (): string {
		return uniqueId('temp_') + '_' + (new Date()).getTime();
	}

	isTempId (id: string): boolean {
		return /^temp_/.test(id);
	}

	paginate<T> (array: T[], pageNumber: number, pageSize: number): T[] {
		--pageNumber; // because pages logically start with 1, but technically with 0
		return slice(array, pageNumber * pageSize, (pageNumber + 1) * pageSize);
	}

	getTextWidth (text: string, font: string): number {
		if (!text) {
			return 0;
		}

		this.hiddenDiv.style.font = font;
		this.hiddenDiv.innerHTML = text;

		return Math.ceil(this.hiddenDiv.getBoundingClientRect().width);
	}

	addMicrotask (callBack: CallableFunction): void {
		if (!!window.queueMicrotask) {
			queueMicrotask(() => callBack());
		} else {
			Promise.resolve()
				.then(() => callBack());
		}
	}

	compareModelCollection<T> (model: T, params: ICommonServiceCompareModelParams<T>): void {
		if (params.originalItems) {
			params.originalItems = cloneDeep(params.originalItems);
		}

		const defaultOptions: ICommonServiceCompareModelParams<T> = {
			itemsField: '', // The name of a field with the changed collection
			addedItemsField: '', // The name of a field with new elements
			deletedItemsField: '', // The name of a field with deleted elements
			originalItems: [], // Copy of an old array
			fieldId: 'Id', // Field of comparing of elements
		};

		defaults(params, defaultOptions);
		const items = cloneDeep(model[params.itemsField]);

		if (!isEqual(model[params.itemsField], params.originalItems)) {
			if (model[params.itemsField] && model[params.itemsField].length) {
				if (!isEmpty(params.originalItems)) {
					for (let i = 0; i < model[params.itemsField].length; i++) {
						for (let j = 0; j < params.originalItems!.length; j++) {
							if (params.originalItems![j][params.fieldId] === model[params.itemsField][i][params.fieldId]) {
								params.originalItems!.splice(j, 1);
								j--;
								model[params.itemsField].splice(i, 1);
								i--;
								break;
							}
						}
					}
					model[params.addedItemsField] = isEqual(model[params.addedItemsField], model[params.itemsField]) ?
						[] :
						model[params.itemsField];
					model[params.deletedItemsField] = params.originalItems;
					model[params.itemsField] = items;
				} else {
					model[params.addedItemsField] = isEqual(model[params.addedItemsField], model[params.itemsField]) ?
						[] :
						model[params.itemsField];
				}
			} else if (!isEmpty(params.originalItems)) {
				model[params.deletedItemsField] = params.originalItems;
			}
		}
	}

	customMerge<T> (
		x: T[] | T,
		y: T[] | T,
	): T[] | T {
		if (isUndefined(x)) {
			return y;
		}

		if (isUndefined(y) || isNull(y) || isNull(x)) {
			return x;
		}

		if (isArray(x) && isArray(y)) {
			return y;
		}

		if (isObject(x) && isObject(y)) {
			return merge(x, y, this.customMerge);
		} else {
			return y;
		}
	}

	customConcat<T> (
		arr1: T[],
		arr2: T[],
		outputOptions?: { GroupByKey: string; GroupItemsKey: string },
	): T[] {
		if (!isEmpty(arr1) && !isEmpty(arr2) && outputOptions) {
			const groupByKey = outputOptions.GroupByKey;
			const groupItemsKey = outputOptions.GroupItemsKey;

			arr2.forEach((arr2group) => {
				const arr1group = find(arr1, (arr1gr) => arr1gr[groupByKey].Id === arr2group[groupByKey].Id);

				if (arr1group) {
					Array.prototype.push.apply(arr1group[groupItemsKey], arr2group[groupItemsKey]);
				} else {
					arr1.push(arr2group);
				}
			});
		} else {
			Array.prototype.push.apply(arr1, compact(arr2));
		}

		return arr1;
	}

	insertionSortWithIgnoreCase<T> (array: T[], field: string): T[] {
		for (let i = 0; i < array.length; i++) {
			const temp = array[i];
			let j = i - 1;

			while (j >= 0 && get(array[j], field) && (get(array[j], field).toLowerCase() > get(temp, field).toLowerCase())) {
				array[j + 1] = array[j];
				j--;
			}
			array[j + 1] = temp;
		}

		return array;
	}

	pushWithKeyCheck<T> (arr: T[], item: T, key: string): void {
		const found = find(arr, (el) => el[key] === item[key]);

		if (!found) {
			arr.push(item);
		}
	}

	updateTreeValidity (group: UntypedFormGroup | UntypedFormArray): void {
		Object.keys(group.controls).forEach((key: string) => {
			const abstractControl = group.controls[key];

			if (abstractControl instanceof UntypedFormGroup || abstractControl instanceof UntypedFormArray) {
				this.updateTreeValidity(abstractControl);
			} else {
				abstractControl.updateValueAndValidity({ onlySelf: true });
			}
		});
	}
}

export const commonUtilitiesCoreService = new CommonUtilitiesCore();
